import { useMutation, useQuery } from '@tanstack/react-query';
import * as React from 'react';

import MentionCommentBox, { OnAddMentionCommentProps } from 'DesignSystem/features/comment/MentionCommentBox';
import track from 'features/Analytics/track';
import { ATTACHMENT_QUERIES, AttachmentWithKey, postAttachment } from 'features/Attachments/api';
import { addCardComment, prefCardCommentsQuery } from 'pages/preference-cards/api';
import { queryClient } from 'utils/api';
import { getUsersByModuleQuery } from 'utils/api/users';

type RequestParams = {
  id: string;
  message: string;
  mentions: string[];
};
function wrappedMutationFn() {
  return async function makeRequest({ id, message, mentions }: RequestParams) {
    return await addCardComment(id, message, null, mentions);
  };
}

interface Props {
  cardId: string;
}

export default function AddComment({ cardId }: Props) {
  const { data } = useQuery(getUsersByModuleQuery('Physician+Preference+Cards'));
  const users = React.useMemo(() => {
    if (!data) return [];
    return data.users.map((user) => ({ id: user.id, display: user.name, email: user.email_address }));
  }, [data]);
  const mutateComment = useMutation(wrappedMutationFn(), {
    onSuccess: () => {
      void queryClient.invalidateQueries(prefCardCommentsQuery(cardId));
    },
  });

  const mutateAttachment = useMutation(postAttachment, {
    onSuccess: () => {
      void queryClient.invalidateQueries([ATTACHMENT_QUERIES.get]);
    },
  });

  async function handleAddComment({
    comment: message,
    attachments,
    onComplete,
    onUploadError,

    mentions,
  }: OnAddMentionCommentProps) {
    if (attachments && attachments.length > 0) {
      try {
        const comment = await mutateComment.mutateAsync({ id: cardId, message, mentions });

        if ('message' in comment) {
          const keys = attachments
            .map((a) => ({ key: a.key, file: a.file }))
            .filter((a): a is AttachmentWithKey => typeof a.key === 'string')
            .map((attachment) => ({
              key: attachment.key,
              contentType: attachment.file.type,
              contentLength: attachment.file.size,
            }));
          await mutateAttachment.mutateAsync({
            conversationId: comment.conversation_message_id,
            keys,
          });
        }
        track.pcm.addComment('card', attachments.length);
        onComplete();
      } catch (err) {
        console.error('Unexpected error while uploading', err);
        onUploadError('Unexpected error while uploading');
      }
    } else {
      await mutateComment.mutateAsync({ id: cardId, message, mentions });
      track.pcm.addComment('card', 0);
      onComplete();
    }
  }
  return <MentionCommentBox users={users} disabled={mutateComment.isLoading} onAddComment={handleAddComment} />;
}
